import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { config } from '@platform/constants';
import { httpService } from '@shared/utilities';
import { AscResponse } from '@shared/models';
import * as Response from '@shared/models/analysis/analysis.response.type';
import * as Request from '@shared/models/analysis/analysis.request.type';
import { formatErrorMessage } from '@ascendnow/ascend-components';
import { setAnalysisProjects, setAnalysisSingleProject, setSkillList } from '../slices/project.slice';

export const AnalysisProjectEndpoints = {
    getProjects: config.base_url + '/analysis/project',
    getSingleProject: (projectId: string) => config.base_url + `/analysis/project/${projectId}`,
    createProject: config.base_url + '/analysis/project',
    uploadProjectResults: (projectId: string) => config.base_url + `/analysis/project/${projectId}/results`,
    editProjectResults: (resultId: string) => config.base_url + `/analysis/project/result/${resultId}`,
    uploadProjectImages: (projectId: string) => config.base_url + `/analysis/project/${projectId}/images`,
    editProjectImage: (imageId: string) => config.base_url + `/analysis/project/images/${imageId}`,
    getProjectSkillList: config.base_url + '/analysis/skills/list',
    deleteProject: (projectId: string) => config.base_url + `/analysis/project/${projectId}`,
};

export const fetchAnalysisProjects = createAsyncThunk<void, Request.IFetchProjectListQueryType>(
    AnalysisProjectEndpoints.getProjects,
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.IFetchProjectsType>>(
                AnalysisProjectEndpoints.getProjects
            );

            dispatch(setAnalysisProjects(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching projects of analysis, query: ${AnalysisProjectEndpoints.getProjects}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const createOrEditProject = createAsyncThunk<void, Request.ICreateProjectQueryType>(
    AnalysisProjectEndpoints.createProject + '/create',
    async (query, { dispatch }) => {
        try {
            let res = undefined;
            if (query.params.projectId) {
                res = res = await httpService.patch<AscResponse<Response.ICreateProjectResponseType>>(
                    `${AnalysisProjectEndpoints.createProject}/${query.params.projectId}`,
                    query.params
                );
            } else {
                delete query.params.projectId;
                res = await httpService.post<AscResponse<Response.ICreateProjectResponseType>>(
                    `${AnalysisProjectEndpoints.createProject}`,
                    query.params
                );
            }

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error creating a project on analysis, query ${AnalysisProjectEndpoints.createProject}`
                ),
                { type: 'error' }
            );
        } finally {
            if (query.cb && query.cb.onFinally) {
                query.cb.onFinally();
            }
        }
    }
);

export const uploadProjectResults = createAsyncThunk<void, Request.IUploadProjectResultQueryType>(
    AnalysisProjectEndpoints.uploadProjectResults('id'),
    async (query, { dispatch }) => {
        try {
            let res;
            const formData = new FormData();

            if (query.params.resultId) {
                query.params.formData.forEach((pResult, index) => {
                    formData.append(`icon`, pResult.icon);
                    formData.append(`description`, pResult.description);
                    return formData;
                });
                res = await httpService.patch<AscResponse<Response.ICreateProjectResponseType>>(
                    `${AnalysisProjectEndpoints.editProjectResults(query.params.resultId)}`,
                    formData
                );
            } else {
                query.params.formData.forEach((pResult, index) => {
                    formData.append(`icon`, pResult.icon[0]);
                    formData.append(`description[${index}]`, pResult.description);
                    return formData;
                });
                res = await httpService.post<AscResponse<Response.ICreateProjectResponseType>>(
                    `${AnalysisProjectEndpoints.uploadProjectResults(query.params.projectId)}`,
                    formData
                );
            }

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error uploading a project results on analysis, query ${AnalysisProjectEndpoints.uploadProjectResults(
                        'id'
                    )}`
                ),
                { type: 'error' }
            );
        } finally {
            if (query.cb && query.cb.onFinally) {
                query.cb.onFinally();
            }
        }
    }
);

export const uploadProjectImages = createAsyncThunk<void, Request.IUploadProjectImagesQueryType>(
    AnalysisProjectEndpoints.uploadProjectImages('id'),
    async (query, { dispatch }) => {
        try {
            let res;
            const formData = new FormData();
            if (query.params.imageId) {
                formData.append('image', query.params.images[0]);

                res = await httpService.patch<AscResponse<Response.ICreateProjectResponseType>>(
                    `${AnalysisProjectEndpoints.editProjectImage(query.params.imageId)}`,
                    formData
                );
            } else {
                if (query.params.isEdit) {
                    formData.append('images', query.params.images[0]);
                } else {
                    formData.append('images', query.params.images[0][0]);
                    formData.append('images', query.params.images[1][0]);
                }

                res = await httpService.post<AscResponse<Response.ICreateProjectResponseType>>(
                    `${AnalysisProjectEndpoints.uploadProjectImages(query.params.projectId)}`,
                    formData
                );
            }

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error uploading a project images on analysis, query ${AnalysisProjectEndpoints.uploadProjectImages(
                        'id'
                    )}`
                ),
                { type: 'error' }
            );
        } finally {
            if (query.cb && query.cb.onFinally) {
                query.cb.onFinally();
            }
        }
    }
);

export const fetchSingleProject = createAsyncThunk<void, Request.IFetchSingleProjectQueryType>(
    AnalysisProjectEndpoints.getSingleProject('id'),
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.IFetchSingleProjectType>>(
                AnalysisProjectEndpoints.getSingleProject(query.params.projectId)
            );

            dispatch(setAnalysisSingleProject(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching single project of analysis, query: ${AnalysisProjectEndpoints.getSingleProject(
                        'id'
                    )}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const fetchSkillsForProjectCreation = createAsyncThunk<void, Request.IFetchProjectListQueryType>(
    AnalysisProjectEndpoints.getProjectSkillList,
    async (query, { dispatch }) => {
        try {
            const res = await httpService.get<AscResponse<Response.IProjectSkillList>>(
                AnalysisProjectEndpoints.getProjectSkillList
            );

            dispatch(setSkillList(res.data.data));

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error fetching project skill list, query: ${AnalysisProjectEndpoints.getProjectSkillList}`
                ),
                { type: 'error' }
            );
        }
    }
);

export const deleteProject = createAsyncThunk<void, Request.IDeleteProject>(
    AnalysisProjectEndpoints.deleteProject('id'),
    async (query, { dispatch }) => {
        try {
            const res = await httpService.delete<AscResponse<{ isDeleted: boolean }>>(
                `${AnalysisProjectEndpoints.deleteProject(query.params.projectId)}`
            );

            if (res.data) toast.success('The project has been successfully deleted');

            if (query.cb && query.cb.onSuccess) {
                query.cb.onSuccess(res.data.data);
            }
        } catch (err) {
            if (query.cb && query.cb.onError) {
                query.cb.onError(err);
            }
            console.log(
                formatErrorMessage(
                    err,
                    `Error deleting a project, query: ${AnalysisProjectEndpoints.deleteProject('id')}`
                ),
                { type: 'error' }
            );
        }
    }
);
